import React from 'react';
import { FiUsers } from 'react-icons/fi';
import { confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { OwnerEntity, resolveEntities } from '../utils/ownerEntities';
import './FamilyBusinessesPage.css';
import './GLChartOfAccountsTab.css';

// =============================================
// Picker for whose books are being set up, shown as tiles — the same tiles as
// Family & Businesses › Your Family. Used by the GL COA tabs (Type of Business,
// Setup GL Accounts) in place of a dropdown.
//
// People are listed beside businesses: an individual can have an activity that needs
// its own accounts for the tax return (rental income, a sole proprietorship with no
// registered business). A person's type is kept on users.industry_type_id, a
// business's on user_businesses.industry_type_id.
// =============================================
export interface CoaSetupEntity extends OwnerEntity {
  /** owner_type + owner_id: user ids and business ids overlap. */
  key: string;
  is_person: boolean;
  industry_type_id: number | null;
  /** Every type chosen, main activity first (industry_type_id is the first of them). */
  industry_type_ids: number[];
  /** The person's nickname or the business's acronym, shown in the tile's circle. */
  nickname: string;
}

const BUSINESS_OWNER_TYPES = ['business', 'client_business'];

// industry_type_ids (migration 130) holds every type; a row saved before it has only
// the single industry_type_id.
function typeIdsOf(row: Record<string, any> | undefined): number[] {
  const list = row?.industry_type_ids;
  if (Array.isArray(list) && list.length) return list.map(Number).filter(n => n > 0);
  const v = row?.industry_type_id;
  return v == null || v === '' ? [] : [Number(v)];
}

// Same order as the Family & Businesses tiles: the account owner (or the client) first,
// then spouse, children and everyone else by name, then the businesses by name.
function sortRank(e: OwnerEntity, isPerson: boolean, row: Record<string, any> | undefined, index: number): number {
  if (!isPerson) return 4;
  if (index === 0) return 0; // resolveEntities puts the owner / the client first
  const rel = String(row?.relationship || '').trim().toLowerCase();
  if (rel === 'spouse' || rel === 'partner') return 1;
  if (rel === 'child' || rel === 'son' || rel === 'daughter') return 2;
  return 3;
}

/** Everyone whose books can be set up in this view: the people, then the businesses. */
export function getCoaSetupEntities(
  viewMode: 'owner' | 'clients',
  accountOwnerId: number,
  selectedClientId: number | string | null,
  users: Record<string, any>[],
  businesses: Record<string, any>[],
): CoaSetupEntity[] {
  return resolveEntities(viewMode, accountOwnerId, selectedClientId, users, businesses)
    .map((e, index) => {
      const isPerson = !BUSINESS_OWNER_TYPES.includes(e.owner_type);
      const row = isPerson
        ? users.find((u: any) => Number(u.user_id) === e.owner_id)
        : businesses.find((b: any) => Number(b.business_id) === e.owner_id);
      const entity: CoaSetupEntity = {
        ...e,
        key: `${e.owner_type}:${e.owner_id}`,
        is_person: isPerson,
        industry_type_id: typeIdsOf(row)[0] ?? null,
        industry_type_ids: typeIdsOf(row),
        nickname: String((isPerson ? row?.nickname : row?.busi_acronym) || '').trim(),
      };
      return { entity, rank: sortRank(e, isPerson, row, index) };
    })
    .sort((x, y) => x.rank - y.rank ||
      x.entity.display_name.localeCompare(y.entity.display_name, undefined, { sensitivity: 'base' }))
    .map(x => x.entity);
}

interface Props {
  entities: CoaSetupEntity[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** Name of the type set on an entity, for the second line of its tile. */
  typeName: (typeId: number) => string | undefined;
  /** Replaces the type line of a tile (Masters → Taxes shows the province there). */
  meta?: (e: CoaSetupEntity) => string;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] || '?') + (words[1]?.[0] || '')).toUpperCase();
}

const BusinessPickerTiles: React.FC<Props> = ({ entities, selectedKey, onSelect, typeName, meta }) => {
  // Guard-then-land: picking someone else drops what was being edited for the current one.
  const pick = async (key: string) => {
    if (key === selectedKey) return;
    if (!(await confirmDiscardChanges())) return;
    onSelect(key);
  };

  const people = entities.filter(e => e.is_person).length;
  const businesses = entities.length - people;
  const count = [
    people ? `${people} ${people === 1 ? 'person' : 'people'}` : '',
    businesses ? `${businesses} ${businesses === 1 ? 'business' : 'businesses'}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div className="coa-biz-picker">
      <div className="fb-family-count coa-biz-picker-count">
        <FiUsers size={16} /> {count}
      </div>
      <div className="fb-family-list fb-family-grid">
        {entities.map(e => {
          const type = e.industry_type_ids.map(typeName).filter(Boolean).join(' · ');
          return (
            <button
              key={e.key}
              type="button"
              className={`fb-family-card coa-biz-tile ${e.key === selectedKey ? 'selected' : ''}`}
              aria-pressed={e.key === selectedKey}
              onClick={() => pick(e.key)}
            >
              <div className="fb-family-card-avatar fb-biz-avatar">{e.nickname || initials(e.display_name)}</div>
              <div className="fb-family-card-info">
                <div className="fb-family-card-name">{e.display_name}</div>
                <div className="fb-family-card-relationship">{e.badge}</div>
                <div className="fb-family-card-meta">
                  {meta ? meta(e) : (type || (e.is_person ? 'No activity type set' : 'Type not set'))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BusinessPickerTiles;
